// Navigation links for sidebar/menu
export const appLinks = [
  {
    label: 'Practitioners',
    path: '/practitioners',
    icon: 'badge',
    roles: ['hospitaladmin', 'healthcareadmin']
  },
  // ...add other links as needed
];
import { ActivatedRoute, UrlSegment, UrlTree } from '@angular/router';
import { ErrorService } from '../error.service';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../../app.config';
import { Patient, Practitioner } from 'fhir/r5';
import { HttpContext } from '@angular/common/http';
import { DisableCountParam, DisableSortParam } from '../../loading.interceptor';
// auth.service.ts

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, tap, map, catchError } from 'rxjs';
import { Router } from '@angular/router';
import { SessionService, SessionData } from './session.service';
import { PermissionService, ParsedPermissions } from '../../services/permission.service';
import { authBackendEndPointToken } from '../../app.config';
import { NotificationService } from '../../services/notification.service';
import { isHospitalAdmin } from '../system-roles';
export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  roles: string[];
  accountType?: string;
  isApproved: boolean;
  organizationId?: string;
  fhirResourceId?: string;
  isSuperAdmin?: boolean;
  isDefaultPassword?: boolean;
  userId?: string;
  userType?: string;
  display?: string;
  hospitalId?: string;
  rolePermissionsJson?: string;
  photo?: Array<{ url: string }>;
  telecom?: any[];
  address?: any[];
    isGlobal?: boolean;  
  activeHospitalFhirId?: string; 
  activeHospitalName?: string; 
  gender?: string;
  birthDate?: string;
  qualification?: any[];
  [key: string]: any;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User & {
    fhirResource?: any;
    isDefaultPassword?: boolean;
  };
}

export interface RegisterRequest {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  role?: string;
  organizationId?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

interface RoleConfiguration {
  RoleName: string;
  Specialty: string | null;
  HasFullAccess: boolean;
  Permissions: {
    AllowedServiceRequestCategories: string[];
    AllowedObservationCategories: string[];
    CustomPermissions: {
      CanViewAllPatients: boolean;
      CanOrderTests: boolean;
      CanPrescribeMedications: boolean;
      CanPerformProcedures: boolean;
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authBackendUrl = inject(authBackendEndPointToken);
  private backendUrl = inject(backendEndPointToken);
  private terminologyUrl = inject(terminologyCanonicalUrlBaseToken);

  user = new BehaviorSubject<User | null>(null);
  triedUrl: string = '';
  private _cachedPermissions: ParsedPermissions | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private sessionService: SessionService,
    private notificationService: NotificationService,
    private permissionService: PermissionService
  ) {
    this.initializeAuth();
  }

  private initializeAuth(): void {
    const session = this.sessionService.getSession();
    if (session?.isLoggedIn && session.id) {
      const originalRole = session.role.toLowerCase();
      const normalizedRoles = (session.roles || []).map(r => r.toLowerCase());

      this.user.next({
        id: session.userId || session.id,
        userId: session.userId || session.id,
        email: session.workEmail,
        name: session.name,
        role: originalRole,
        roles: normalizedRoles,
        userType: originalRole,
        display: session.name,
        hospitalId: session.hospitalId,
        organizationId: session.organizationId,
        fhirResourceId: session.fhirResourceId,
        isSuperAdmin: session.isSuperAdmin,
        rolePermissionsJson: session.rolePermissionsJson,
        isGlobal: session.isGlobal,                         
      activeHospitalFhirId: session.activeHospitalFhirId,  
      activeHospitalName: session.activeHospitalName, 
        isApproved: true
      });
    }
  }

  private extractValue(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj === 'object' && 'value' in obj) {
      return obj.value;
    }
    return obj;
  }

  private flattenFhirResource(fhirResource: any): any {
    if (!fhirResource) return {};

    const flattened: any = {};

    if (fhirResource.id) {
      flattened.fhirId = this.extractValue(fhirResource.id);
    }

    if (fhirResource.active) {
      flattened.active = this.extractValue(fhirResource.active);
    }

    if (fhirResource.gender) {
      flattened.gender = this.extractValue(fhirResource.gender);
    }

    if (fhirResource.birthDate) {
      flattened.birthDate = this.extractValue(fhirResource.birthDate);
    }

    if (fhirResource.photo && Array.isArray(fhirResource.photo)) {
      flattened.photo = fhirResource.photo.map((p: any) => ({
        url: this.extractValue(p.url),
        contentType: this.extractValue(p.contentType),
        title: this.extractValue(p.title)
      }));
    }

    if (fhirResource.telecom && Array.isArray(fhirResource.telecom)) {
      flattened.telecom = fhirResource.telecom.map((t: any) => ({
        system: this.extractValue(t.system),
        value: this.extractValue(t.value),
        use: this.extractValue(t.use)
      }));
    }

    if (fhirResource.address && Array.isArray(fhirResource.address)) {
      flattened.address = fhirResource.address.map((a: any) => ({
        line: a.line?.map((l: any) => this.extractValue(l)),
        city: this.extractValue(a.city),
        state: this.extractValue(a.state),
        postalCode: this.extractValue(a.postalCode),
        country: this.extractValue(a.country)
      }));
    }

    if (fhirResource.name && Array.isArray(fhirResource.name)) {
      flattened.name = fhirResource.name.map((n: any) => ({
        text: this.extractValue(n.text),
        family: this.extractValue(n.family),
        given: n.given?.map((g: any) => this.extractValue(g)),
        prefix: n.prefix?.map((p: any) => this.extractValue(p))
      }));
    }

    if (fhirResource.qualification && Array.isArray(fhirResource.qualification)) {
      flattened.qualification = fhirResource.qualification.map((q: any) => ({
        code: q.code ? {
          text: this.extractValue(q.code.text)
        } : null,
        issuer: q.issuer ? {
          display: this.extractValue(q.issuer.display)
        } : null
      }));
    }

    return flattened;
  }
  get currentUserValue(): User | null {
    return this.user.getValue();
  }

  get currentUser$(): Observable<User | null> {
    return this.user.asObservable();
  }
  refreshToken(): Observable<any> {
    const session = this.sessionService.getSession();

    if (!session?.refreshToken) {
      throw new Error('No refresh token available');
    }

    console.log('🔄 Sending refresh token request...');

    return this.http.post(`${this.authBackendUrl}/api/auth/refresh`, {
      refreshToken: session.refreshToken
    });
  }

  register(request: RegisterRequest): Observable<any> {
    return this.http.post(`${this.authBackendUrl}/api/auth/register`, request);
  }

  getPermissions(): ParsedPermissions | null {
    const currentUser = this.user.getValue();
// $#$    // /* alert(`Getting permissions for user: ${JSON.stringify(currentUser?.['roleConfigurations']?.find((e: any) */ => {
    //   console.log(`Checking role config: ${e.RoleName}`);
    //   return e.RoleName === 'alliedhealthprofessional';
    // })?.customPermissions)} `);
    // console.log(currentUser);
// $#$    // /* debugger; */
    if (!currentUser || (currentUser.accountType !== 'Practitioner' && String(currentUser.accountType)?.toLowerCase() !== 'alliedhealthprofessional')) {
      return null;
    }

    if (!this._cachedPermissions && currentUser.rolePermissionsJson) {
      this._cachedPermissions = this.permissionService.parsePermissions(
        currentUser.rolePermissionsJson
      );
    }
// $#$    // /* alert(`Permissions for user: ${JSON.stringify(this._cachedPermissions) */} `);

    return this._cachedPermissions;
  }

  private clearPermissionsCache(): void {
    this._cachedPermissions = null;
  }



  canAccessObservationCategory(category: string): boolean {
    const currentUser = this.user.getValue();

    if (!currentUser) return false;

    const permissions = this.getPermissions();

    return this.permissionService.canAccessObservationCategory(
      permissions,
      category
    );
  }



  // In your auth.service.ts, add these methods:

  /**
   * Get all allowed service request categories for the current user
   * Respects role-based permissions and specialty restrictions
   */
  getAllowedServiceRequestCategories(): string[] {
    const currentUser = this.user.getValue();

    if (!currentUser) {
      return [];
    }

    const role = (currentUser.role || '').toLowerCase().replace(/\s+/g, '');
    const fullAccessRoles = ['hospitaladmin', 'doctor', 'healthcareassistant', 'remotedoctor', 'healthcareadmin', 'cashier',

      'labtechnician','nursemidwife','primaryhealthcareprovider'
    ];
    if (currentUser.isSuperAdmin || fullAccessRoles.includes(role)) {
      return ['laboratory', 'imaging', 'procedure', 'surgery', 'observation', 'others'];
    }

// $#$    // /* alert(currentUser.role.toLowerCase() */);
    // Default Restricted Roles: Lab staff only see laboratory by default
    if (['lab', 'labtechnician'].includes(role)) {
      // If they have JSON, that will be checked next and can override this
      // if (!currentUser.rolePermissionsJson) {
      return ['laboratory'];
      // }

    }

    // Allied Health Professional: default categories if no rolePermissionsJson override
    if (role === 'alliedhealthprofessional') {
      if (!currentUser.rolePermissionsJson) {
        return ['observation', 'procedure', 'imaging', 'others'];
      }
    }

    // If user has rolePermissionsJson (specialty-based permissions)
    if (currentUser.rolePermissionsJson) {
      try {
        const roleConfigs = JSON.parse(currentUser.rolePermissionsJson) as RoleConfiguration[];
        const activeRole = currentUser.role.toLowerCase();

        const roleConfig = roleConfigs.find(rc =>
          rc.RoleName.toLowerCase() === activeRole
        );

        if (!roleConfig) {
          console.warn('⚠️ No role config found for:', activeRole);
          return [];
        }

        // Full access to all categories
        if (roleConfig.HasFullAccess) {
          console.log('✅ Full access granted for:', activeRole);
          return ['laboratory', 'imaging', 'procedure', 'surgery', 'observation', 'others'];
        }

        // Return ONLY the categories from the JSON
        const allowedCategories = roleConfig.Permissions?.AllowedServiceRequestCategories ?? [];
        console.log('📋 Allowed service request categories:', allowedCategories);
// $#$        // /* alert(JSON.stringify(allowedCategories) */);
        return allowedCategories;

      } catch (error) {
        console.error('❌ Failed to parse rolePermissionsJson:', error);
        return [];
      }
    }

    // No permissions JSON = no categories allowed
    console.warn('⚠️ No rolePermissionsJson, returning empty');
    return [];
  }
  // ADD THIS METHOD TO auth.service.ts
  // Place it right after the getUserSpecialty() method (around line 449)

  /**
   * Get the current user's specialty for their active role
   * Returns the specialty if the user is a Doctor, Nurse, or ServiceProvider
   */
  getCurrentUserSpecialty(): string | undefined {
    const currentUser = this.user.getValue();

    if (!currentUser || !currentUser.rolePermissionsJson) {
      return undefined;
    }

    const activeRole = currentUser.role.toLowerCase();

    // Only show specialty for these roles
    if (!['doctor', 'remotedoctor', 'healthcareassistant', 'alliedhealthprofessional','nursemidwife','primaryhealthcareprovider'].includes(activeRole)) {
      return undefined;
    }

    try {
      const roleConfigs = JSON.parse(currentUser.rolePermissionsJson) as RoleConfiguration[];

      const roleConfig = roleConfigs.find(rc =>
        rc.RoleName.toLowerCase() === activeRole
      );

      return roleConfig?.Specialty || undefined;
    } catch (error) {
      console.error('Failed to parse rolePermissionsJson for specialty:', error);
      return undefined;
    }
  }
  /**
   * Check if user can access a specific service request category
   */
  canAccessServiceRequestCategory(category: string): boolean {
    const currentUser = this.user.getValue();

    if (!currentUser) return false;

    // SuperAdmin and Admin have full access
    if (currentUser.isSuperAdmin || isHospitalAdmin(currentUser.role)) {
      return true;
    }

    const allowedCategories = this.getAllowedServiceRequestCategories();
    const normalizedCategory = (category || '').toString().toLowerCase().trim();

    return allowedCategories.some(cat =>
      cat.toLowerCase().trim() === normalizedCategory
    );
  }

  /**
   * Filter service request types based on user permissions
   * Use this to restrict the tab navigation or filter displayed service requests
   */
  getFilteredServiceRequestTypes(allTypes: string[]): string[] {
    const currentUser = this.user.getValue();

    if (!currentUser) {
      return [];
    }

    // SuperAdmin and Admin see everything
    if (currentUser.isSuperAdmin || isHospitalAdmin(currentUser.role)) {
      return allTypes;
    }

    const allowedCategories = this.getAllowedServiceRequestCategories();

    // Map common type names to category names
    const typeToCategory: Record<string, string> = {
      'imaging': 'imaging',
      'procedure': 'procedure',
      'surgery': 'surgery',
      'observation': 'observation',
      'observations': 'observation',
      'vital': 'observation',
      'vitals': 'observation',
      'vital-signs': 'observation',
      'laboratory': 'laboratory',
      'lab': 'laboratory',
      'lab-tests': 'laboratory',
      'others': 'others'
    };

    return allTypes.filter(type => {
      const category = typeToCategory[type.toLowerCase()] || type.toLowerCase();
      return allowedCategories.some(allowed =>
        allowed.toLowerCase() === category.toLowerCase()
      );
    });
  }
  /**
   * Update the permissions cache when role changes
   */
  refreshPermissions(): void {
    this.clearPermissionsCache();
  }

  /**
   * Check multiple categories at once
   */
  canAccessAnyServiceRequestCategory(categories: string[]): boolean {
    return categories.some(cat => this.canAccessServiceRequestCategory(cat));
  }

  /**
   * Check if user has any service request permissions
   */
  hasAnyServiceRequestPermission(): boolean {
    const allowedCategories = this.getAllowedServiceRequestCategories();
    return allowedCategories.length > 0;
  }


  getOrganizationReferenceFromUser(user: any): string | null {
    const orgId = user?.organizationId ?? user?.hospitalId ?? null;

    if (!orgId || typeof orgId !== 'string' || !orgId.trim()) {
      return null;
    }

    return this.normalizeOrganizationReference(orgId);
  }

  normalizeOrganizationReference(idOrRef: string): string {
    const value = String(idOrRef ?? '').trim();
    if (!value) return value;
    if (value.startsWith('Organization/')) return value;
    return `Organization/${value}`;
  }

  determineHospitalId(user: any): string {
    if (user?.organizationId) {
      return user.organizationId;
    }
    if (user?.hospitalId) {
      return user.hospitalId;
    }
    return '889ca6048-a0a7-4572-8519-0c4c41d1a5b6';
  }

  getUserSpecialty(roleName: string = 'Doctor'): string | undefined {
    const permissions = this.getPermissions();
    return this.permissionService.getSpecialty(permissions, roleName);
  }

  canOrderTests(): boolean {
    const currentUser = this.user.getValue();
    if (!currentUser) return false;

    const permissions = this.getPermissions();
    return permissions?.customPermissions.canOrderTests ||
      permissions?.hasFullAccess ||
      false;
  }
  /**
   * Check if user can prescribe medications
   */
  canPrescribeMedications(): boolean {
    const permissions = this.getPermissions();
    if (['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'].some(role => this.user?.getValue()?.role.includes(role))) return true;
    return permissions?.customPermissions?.canPrescribeMedications || permissions?.hasFullAccess || false;
  }

  /**
   * Check if user can perform procedures
   */
  canPerformProcedures(): boolean {
    const permissions = this.getPermissions();
    return permissions?.customPermissions?.canPerformProcedures || permissions?.hasFullAccess || false;
  }
  login(request: LoginRequest): Observable<LoginResponse>;
  login(email: string, password: string): Observable<LoginResponse>;

  login(requestOrEmail: LoginRequest | string, password?: string): Observable<LoginResponse> {
    let loginRequest: LoginRequest;

    if (typeof requestOrEmail === 'string') {
      loginRequest = { email: requestOrEmail, password: password! };
    } else {
      loginRequest = requestOrEmail;
    }

    return this.http.post<LoginResponse>(`${this.authBackendUrl}/api/auth/login`, loginRequest)
      .pipe(
        tap(response => {
          this.checkAndUpdatePractitionerResource(response);
          const originalRole = response.user.role.toLowerCase();
          const normalizedRoles = (response.user.roles || []).map(r => r.toLowerCase());
         // console.log('login response',response)
          const fhirData = response.user.fhirResource
            ? this.flattenFhirResource(response.user.fhirResource)
            : {};

          const effectiveFhirResourceId = response.user.fhirResourceId || fhirData.fhirId;

          const user: User = {
            id: response.user.id,
            userId: response.user.id,
            email: response.user.email,
            name: response.user.name,
            display: response.user.name,
            role: originalRole,
            roles: normalizedRoles,
            userType: response.user.accountType,
            accountType: response.user.accountType,
            hospitalId: response.user.organizationId || '',
            organizationId: response.user.organizationId,
            fhirResourceId: effectiveFhirResourceId,
            isSuperAdmin: response.user.isSuperAdmin,
            isDefaultPassword: response.user.isDefaultPassword,
            isApproved: response.user.isApproved,
            rolePermissionsJson: response.user.rolePermissionsJson,
              isGlobal: response.user.isGlobal,         
          activeHospitalFhirId: response.user.activeHospitalFhirId, 
          activeHospitalName: response.user.activeHospitalName, 
            photo: fhirData.photo,
            telecom: fhirData.telecom,
            address: fhirData.address,
            gender: fhirData.gender,
            birthDate: fhirData.birthDate,
            qualification: fhirData.qualification
          };

          this.sessionService.setSession({
            id: response.user.id,
            userId: response.user.id,
            workEmail: response.user.email,
            name: response.user.name,
            accountType: response.user.accountType,
            role: originalRole,
            roles: normalizedRoles,
            token: response.accessToken,
            accessToken: response.accessToken,
            idToken: response.accessToken,
            hospitalId: response.user.organizationId || '',
            organizationId: response.user.organizationId,
            fhirResourceId: effectiveFhirResourceId,
            isSuperAdmin: response.user.isSuperAdmin,
            isDefaultPassword: response.user.isDefaultPassword,
            refreshToken: response.refreshToken,
            rolePermissionsJson: response.user.rolePermissionsJson,
            isGlobal: response.user.isGlobal,  
          activeHospitalFhirId: response.user.activeHospitalFhirId, 
          activeHospitalName: response.user.activeHospitalName, 
            isLoggedIn: true,
            loginMethod: 'credentials'
          });

          localStorage.setItem('refreshToken', response.refreshToken);
          if (response.user.organizationId) {
            localStorage.setItem('fhir_hospital_id', response.user.organizationId);
          }

          this.user.next(user);
          this.notificationService.startConnection(response.accessToken);
        })
      );
  }

  private checkAndUpdatePractitionerResource(response: LoginResponse): void {
    const fhirId = response.user.fhirResourceId;
    if (!fhirId) {
      console.log('[DEBUG-AUTH] No fhirResourceId found in login response.');
      return;
    }
    
    // Fetch the standard FHIR resource to avoid .value wrappers from login payload
    this.http.get<any>(`${this.backendUrl}/Practitioner/${fhirId}`).subscribe({
      next: (practitioner) => {
        console.log('[DEBUG-AUTH] Fetched Practitioner from backend:', practitioner);
        let needsUpdate = false;
        
        if (!practitioner.resourceType) {
           practitioner.resourceType = 'Practitioner';
        }
        
        // 1. Check identifier
        const sidSystem = 'https://elikita-server.daalitech.com/fhir/identifier/sid';
        if (!practitioner.identifier) practitioner.identifier = [];
        const hasSid = practitioner.identifier.some((id: any) => id.system === sidSystem);
        
        if (!hasSid) {
          console.log('[DEBUG-AUTH] Practitioner missing SID identifier. Adding 0000.');
          practitioner.identifier.push({
            system: sidSystem,
            value: '0000'
          });
          needsUpdate = true;
        } else {
          console.log('[DEBUG-AUTH] Practitioner already has SID identifier.');
        }

        // 2. Check meta.tag
        const tenantTagSystem = `${this.terminologyUrl}/CodeSystem/organizationTenant`;
        const orgId = response.user.organizationId;

        if (orgId) {
          if (!practitioner.meta) practitioner.meta = {};
          if (!practitioner.meta.tag) practitioner.meta.tag = [];
          
          const hasTenantTag = practitioner.meta.tag.some((tag: any) => tag.system === tenantTagSystem);
          
          if (!hasTenantTag) {
            console.log(`[DEBUG-AUTH] Practitioner missing tenant tag. Adding orgId: ${orgId}`);
            practitioner.meta.tag.push({
              system: tenantTagSystem,
              code: orgId
            });
            needsUpdate = true;
          } else {
            console.log('[DEBUG-AUTH] Practitioner already has tenant tag.');
          }
        }

        if (needsUpdate) {
          console.log('[DEBUG-AUTH] Practitioner needs update. Sending PUT request to FHIR server.', practitioner);
          this.http.put(`${this.backendUrl}/Practitioner/${practitioner.id}`, practitioner).subscribe({
            next: () => console.log('[DEBUG-AUTH] Practitioner updated successfully.'),
            error: (err) => console.error('[DEBUG-AUTH] Failed to update Practitioner:', err)
          });
        } else {
          console.log('[DEBUG-AUTH] No updates needed for Practitioner resource.');
        }
      },
      error: (err) => console.error('[DEBUG-AUTH] Failed to fetch Practitioner for validation:', err)
    });
  }

  getSession(): SessionData | null {
    return this.sessionService.getSession();
  }

  getCurrentUser(): User | null {
    const session = this.sessionService.getSession();
    if (!session || !session.isLoggedIn) {
      return null;
    }

    const originalRole = session.role.toLowerCase();
    const normalizedRoles = (session.roles || []).map(r => r.toLowerCase());

    return {
      id: session.userId || session.id,
      userId: session.userId || session.id,
      email: session.workEmail,
      name: session.name,
      role: originalRole,
      roles: normalizedRoles,
      accountType: session.accountType,
      userType: session.accountType,
      display: session.name,
      hospitalId: session.hospitalId,
      organizationId: session.organizationId,
      fhirResourceId: session.fhirResourceId,
      isSuperAdmin: session.isSuperAdmin,
      isDefaultPassword: session.isDefaultPassword,
      rolePermissionsJson: session.rolePermissionsJson,
      isGlobal: session.isGlobal,  
      activeHospitalFhirId: session.activeHospitalFhirId,
      activeHospitalName: session.activeHospitalName, 

      isApproved: true
    };
  }

  can(resource: string, action: string, category?: string): boolean {

    const currentUser = this.user.getValue();
    if (!currentUser || !currentUser.role) {
      return false;
    }
// $#$    // /* alert(currentUser.role); */

    // if (currentUser.isSuperAdmin || currentUser.role === 'hospitaladmin') {
    //   return true;
    // }

// $#$    // /* alert(category); */
    //if the role=serviceprovider, we assume that the user has rolePermissionsJson with specialty-based permissions. We check those permissions for service providers instead of the static role-based permissions
    if (currentUser.role.toLowerCase() === 'alliedhealthprofessional' && (action === 'add' || action === 'request')
      && (resource === 'serviceRequest' || resource === 'labRequest')) {
      const permissions = this.getPermissions();
// $#$      // /* alert(`Permissions for service provider: ${JSON.stringify(permissions) */}`);
      if (permissions) {
        // console.log(permissions);
        const serviceProviderConfig = permissions['roleConfigurations'].find(
          (role: any) => role.RoleName === 'alliedhealthprofessional'
        );
// $#$        //  /* alert(`Service provider permissions: ${JSON.stringify(serviceProviderConfig?.permissions) */}`);

        return (serviceProviderConfig as any)?.Permissions?.CustomPermissions?.CanOrderTests;

// $#$        // /* debugger; */
      }
      return false;
    }
    // if (currentUser.accountType === 'Practitioner' && currentUser.rolePermissionsJson) {
    //   if (this.hasFullAccessForResource(resource)) {
    //     return true;
    //   }

    //   if (category && currentUser.role === 'alliedhealthprofessional') {
    //     if (resource === 'observation') {
    //       return this.canAccessObservationCategory(category);
    //     }
    //     if (resource === 'serviceRequest') {
    //       return this.canAccessServiceRequestCategory(category);
    //     }
    //   }
    // }

    if (currentUser.role.toLowerCase() === 'alliedhealthprofessional' && resource === 'task' && (action === 'fulfill' || action === 'complete')) {
      if (category) {
        return this.canAccessServiceRequestCategory(category) || this.canAccessObservationCategory(category);
      }
      return true;
    }

    const userRole = currentUser.role;
    const permissions = this.getRolePermissions(userRole);

    if (permissions.includes('*')) {
      return true;
    }

    const permission = `${resource}:${action}`;
    // return permissions.includes(permission);
    return capacityObject[resource]?.[action]?.includes(userRole) ?? false;
  }

  private hasFullAccessForResource(resource: string): boolean {
    const currentUser = this.user.getValue();

    if (!currentUser || !currentUser.rolePermissionsJson) {
      return false;
    }

    try {
      const roleConfigs = JSON.parse(currentUser.rolePermissionsJson) as RoleConfiguration[];
      const activeRole = currentUser.role;

      const roleConfig = roleConfigs.find(rc =>
        rc.RoleName.toLowerCase() === activeRole.toLowerCase()
      );

      return roleConfig?.HasFullAccess ?? false;
    } catch (error) {
      console.warn('Failed to parse rolePermissionsJson', error);
      return false;
    }
  }

  getAllowedObservationCategories(): string[] {
    const currentUser = this.user.getValue();

    if (!currentUser) {
      return [];
    }

    // SuperAdmin and Admin have full access
    if (currentUser.isSuperAdmin || isHospitalAdmin(currentUser.role)) {
      return [
        'vital-signs',
        'physical-exam',
        'exam',
        'patient-symptoms',
        'imaging',
        'laboratory',
        'survey',
        'procedure',
        'social-history',
        'nutrition',
        'family-history',
        'device',
        'others'
      ];
    }

    if (currentUser.rolePermissionsJson) {
      try {
        const roleConfigs = JSON.parse(currentUser.rolePermissionsJson) as RoleConfiguration[];
        const activeRole = currentUser.role.toLowerCase();

        const roleConfig = roleConfigs.find(rc =>
          rc.RoleName.toLowerCase() === activeRole
        );

        if (!roleConfig) {
          console.warn('⚠️ No role config found for:', activeRole);
          return [];
        }

        if (roleConfig.HasFullAccess) {
          return [
            'vital-signs',
            'physical-exam',
            'exam',
            'patient-symptoms',
            'imaging',
            'laboratory',
            'survey',
            'procedure',
            'social-history',
            'nutrition',
            'family-history',
            'device',
            'others'
          ];
        }

        const allowedCategories = (roleConfig.Permissions?.AllowedObservationCategories ?? []).slice();

        // Back-compat: Patient symptoms entries are represented as Observation with base category exam/social-history
        // plus a secondary `patient-symptoms` marker category.
        const normalizedAllowed = allowedCategories.map(c => (c ?? '').toString().trim().toLowerCase());
        const alreadyHasPatientSymptoms = normalizedAllowed.includes('patient-symptoms');
        const hasLegacySymptomCategories = normalizedAllowed.includes('exam') || normalizedAllowed.includes('social-history');
        if (!alreadyHasPatientSymptoms && hasLegacySymptomCategories) {
          allowedCategories.push('patient-symptoms');
        }
        console.log('📋 Allowed observation categories:', allowedCategories);

        return allowedCategories;

      } catch (error) {
        console.error('❌ Failed to parse observation categories:', error);
        return [];
      }
    }

    return [];
  }



  getFilteredObservationCategories(allCategories: string[]): string[] {
    const currentUser = this.user.getValue();

    if (!currentUser) {
      return [];
    }

    if (currentUser.isSuperAdmin || isHospitalAdmin(currentUser.role)) {
      return allCategories;
    }

    if (currentUser.accountType === 'Practitioner') {
      const allowedCategories = this.getAllowedObservationCategories();

      if (allowedCategories.length === allCategories.length) {
        return allCategories;
      }

      return allCategories.filter(cat => allowedCategories.includes(cat));
    }

    const userRole = currentUser.role.toLowerCase();
    if (['doctor', 'remotedoctor', 'healthcareassistant', 'labtechnician','nursemidwife','primaryhealthcareprovider'].includes(userRole)) {
      return allCategories;
    }

    return [];
  }

  getOrganizationId(): string | undefined {
    const user = this.user.getValue();
    if (!user) return undefined;

    return user.organizationId ?? user.hospitalId ?? undefined;
  }

  private getRolePermissions(role: string): string[] {
    const ROLE_PERMISSIONS: Record<string, string[]> = {
      'superadmin': ['*'],
      'hospitaladmin': ['*'],
      'pharmacist': [
        'patient:viewAll', 'patient:view',
        'medication:add', 'medication:viewAll', 'medication:view', 'medication:edit', 'medication:update', 'medication:stock',
        'medicationDispense:add', 'medicationDispense:viewAll', 'medicationDispense:view', 'medicationDispense:update', 'medicationDispense:delete',
        'medicationRequest:viewAll', 'medicationRequest:view', 'medicationRequest:dispense', 'medicationRequest:viewDispensed',
        'labRequest:viewAll', 'labRequest:view',
        'invoice:read', 'invoice:generate', 'invoice:pay'
      ],
      'practitioner': [
        'patient:add', 'patient:viewAll', 'patient:edit', 'patient:view',
        'condition:add', 'condition:viewAll', 'condition:edit', 'condition:view',
        'observation:add', 'observation:viewAll', 'observation:edit', 'observation:view',
        'encounter:add', 'encounter:viewAll', 'encounter:update', 'encounter:view',
        'medication:add', 'medication:viewAll', 'medication:edit', 'medication:view',
        'medicationRequest:add', 'medicationRequest:viewAll', 'medicationRequest:update', 'medicationRequest:view',
        'medicationDispense:add', 'medicationDispense:viewAll', 'medicationDispense:view',
        'labRequest:add', 'labRequest:viewAll', 'labRequest:update', 'labRequest:read', 'labRequest:view',
        'specimen:add', 'specimen:viewAll', 'specimen:view',
        'location:add', 'location:viewAll', 'location:update', 'location:view',
        'task:add', 'task:viewAll', 'task:update', 'task:view',
        'codeSystem:add', 'codeSystem:viewAll', 'codeSystem:view',
        'chargeItemDef:add', 'chargeItemDef:viewAll', 'chargeItemDef:view'
      ],
      'doctor': [
        'patient:add', 'patient:viewAll', 'patient:edit', 'patient:view',
        'condition:add', 'condition:viewAll', 'condition:edit', 'condition:view',
        'observation:add', 'observation:viewAll', 'observation:edit', 'observation:view',
        'encounter:add', 'encounter:viewAll', 'encounter:update', 'encounter:view',
        'medication:add', 'medication:viewAll', 'medication:edit', 'medication:view',
        'medicationRequest:add', 'medicationRequest:request', 'medicationRequest:viewAll', 'medicationRequest:update', 'medicationRequest:view',
        'medicationDispense:add', 'medicationDispense:viewAll', 'medicationDispense:view',
        'labRequest:add', 'labRequest:viewAll', 'labRequest:update', 'labRequest:read', 'labRequest:view',
        'specimen:add', 'specimen:viewAll', 'specimen:view',
        'location:add', 'location:viewAll', 'location:update', 'location:view',
        'task:add', 'task:viewAll', 'task:update', 'task:view',
        'codeSystem:add', 'codeSystem:viewAll', 'codeSystem:view',
        'chargeItemDef:add', 'chargeItemDef:viewAll', 'chargeItemDef:view'
      ],
      'healthcareassistant': [
        'patient:viewAll', 'patient:view',
        'observation:add', 'observation:viewAll', 'observation:view',
        'medication:add', 'medication:viewAll', 'medication:view',
        'medicationDispense:add', 'medicationDispense:viewAll', 'medicationDispense:view',
        'medicationRequest:viewAll', 'medicationRequest:view',
        'specimen:add', 'specimen:viewAll', 'specimen:view',
        'encounter:viewAll', 'encounter:view'
      ],
       'primaryhealthcareprovider': [
        'patient:viewAll', 'patient:view',
        'observation:add', 'observation:viewAll', 'observation:view',
        'medication:add', 'medication:viewAll', 'medication:view',
        'medicationDispense:add', 'medicationDispense:viewAll', 'medicationDispense:view',
        'medicationRequest:viewAll', 'medicationRequest:view',
        'specimen:add', 'specimen:viewAll', 'specimen:view',
        'encounter:viewAll', 'encounter:view'
      ],
       'nursemidwife': [
        'patient:viewAll', 'patient:view',
        'observation:add', 'observation:viewAll', 'observation:view',
        'medication:add', 'medication:viewAll', 'medication:view',
        'medicationDispense:add', 'medicationDispense:viewAll', 'medicationDispense:view',
        'medicationRequest:viewAll', 'medicationRequest:view',
        'specimen:add', 'specimen:viewAll', 'specimen:view',
        'encounter:viewAll', 'encounter:view'
      ],
      'patient': [
        'patient:view',
        'observation:view',
        'medication:view',
        'condition:view',
        'encounter:view'
      ],
      'healthcareadmin': [
        'patient:add', 'patient:viewAll', 'patient:edit', 'patient:view',
        'encounter:add', 'encounter:viewAll', 'encounter:view'
      ],
      'labtechnician': [
        'labRequest:viewAll', 'labRequest:update', 'labRequest:read', 'labRequest:view',
        'specimen:add', 'specimen:viewAll', 'specimen:view',
        'observation:add', 'observation:viewAll', 'observation:view'
      ],
      'remotedoctor': [
        'patient:add', 'patient:viewAll', 'patient:edit', 'patient:view',
        'condition:add', 'condition:viewAll', 'condition:edit', 'condition:view',
        'observation:add', 'observation:viewAll', 'observation:edit', 'observation:view',
        'encounter:add', 'encounter:viewAll', 'encounter:update', 'encounter:view',
        'medication:add', 'medication:viewAll', 'medication:edit', 'medication:view',
        'medicationRequest:add', 'medicationRequest:request', 'medicationRequest:viewAll', 'medicationRequest:update', 'medicationRequest:view',
        'medicationDispense:add', 'medicationDispense:viewAll', 'medicationDispense:view',
        'labRequest:add', 'labRequest:viewAll', 'labRequest:update', 'labRequest:read', 'labRequest:view',
        'specimen:add', 'specimen:viewAll', 'specimen:view',
        'location:add', 'location:viewAll', 'location:update', 'location:view',
        'task:add', 'task:viewAll', 'task:update', 'task:view',
        'codeSystem:add', 'codeSystem:viewAll', 'codeSystem:view',
        'chargeItemDef:add', 'chargeItemDef:viewAll', 'chargeItemDef:view'
      ]
    };

    const normalizedRole = role.toLowerCase();
    if (normalizedRole === 'pharmacy') {
      return ROLE_PERMISSIONS['pharmacist'];
    }
    return ROLE_PERMISSIONS[normalizedRole] || [];
  }

  logout(): void {
    this.sessionService.clearSession();
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('fhir_hospital_id');
    this.user.next(null);
    this.clearPermissionsCache();
    this.router.navigate(['/login']);
    this.notificationService.stopConnection();
  }
applySwitchedHospitalSession(
  accessToken: string,
  refreshToken: string,
  hospitalFhirId: string,
  hospitalName?: string
): void {
  if (!accessToken || !refreshToken || !hospitalFhirId) {
    return;
  }

  const currentSession = this.sessionService.getSession();
  const currentUser = this.user.getValue();
  if (!currentSession || !currentUser) {
    return;
  }

  this.sessionService.setSession({
    ...currentSession,
    token: accessToken,
    accessToken,
    idToken: accessToken,
    refreshToken,
    activeHospitalFhirId: hospitalFhirId,
    activeHospitalName: hospitalName
    // organizationId / rolePermissionsJson intentionally untouched —
    // switching hospitals doesn't change the doctor's home org or role config
  });

  localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('fhir_active_hospital_id', hospitalFhirId);

  this.user.next({
    ...currentUser,
    activeHospitalFhirId: hospitalFhirId,
    activeHospitalName: hospitalName
  });

  this.notificationService.startConnection(accessToken);
}


  isAuthenticated(): boolean {
    return this.user.getValue() !== null && this.sessionService.isSessionValid();
  }

 setActiveRole(role: string): Observable<boolean> {
  const currentUser = this.user.getValue();
  const normalizedRole = role.toLowerCase();

  if (!currentUser || !currentUser.roles.includes(normalizedRole)) {
    console.warn('Role not allowed:', role);
    return of(false);
  }

  return this.http.post<{ accessToken: string; refreshToken: string; activeRole: string }>(
    `${this.authBackendUrl}/api/auth/switch-role`,
    { role: normalizedRole }
  ).pipe(
    tap(response => {
      const session = this.sessionService.getSession();
      if (session) {
        const updatedSession: SessionData = {
          ...session,
          role: normalizedRole,
          token: response.accessToken,
          accessToken: response.accessToken,
          idToken: response.accessToken,
          refreshToken: response.refreshToken
        };
        this.sessionService.setSession(updatedSession);
      }

      localStorage.setItem('refreshToken', response.refreshToken);

      const updatedUser: User = { ...currentUser, role: normalizedRole };
      this.user.next(updatedUser);

      this.notificationService.stopConnection();
      this.notificationService.startConnection(response.accessToken);
      this.notificationService.loadUnreadCountForRole(normalizedRole);
      this.notificationService.loadRecentNotificationsByRole(normalizedRole, 10, 0);

      this.clearPermissionsCache(); // role changed → cached permissions are stale
    }),
    map(() => true),
    catchError(err => {
      console.error('setActiveRole failed', err);
      return of(false);
    })
  );
}


}


// Export capacityObject separately - make sure there's no 'return' statement anywhere
export const capacityObject: Record<string, Record<string, string[]>> = {
  task: {
    add: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    start: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    complete: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'alliedhealthprofessional'],
    viewAll: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'alliedhealthprofessional', 'labtechnician'],
    viewSelf: ['patient', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'alliedhealthprofessional', 'labtechnician'],
    update: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    delete: ['hospitaladmin'],
    assign: ['hospitaladmin', 'healthcareadmin', 'labtechnician'],
    export: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    fulfill: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'alliedhealthprofessional'],
  },
  invoice: {
    read: ['hospitaladmin', 'healthcareadmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy', 'patient', 'cashier'],
    generate: ['hospitaladmin', 'healthcareadmin', 'cashier'],
    pay: ['patient', 'cashier'],
  },
  encounter: {
    add: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    //READ - EVE
    start: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    complete: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    viewAll: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'patient'],
    viewSelf: ['patient', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    update: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    delete: ['hospitaladmin'],
    assign: ['hospitaladmin', 'healthcareadmin'],
    export: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider']
  },
  patient: {
    'add': ['hospitaladmin', 'healthcareadmin'],
    'register': ['hospitaladmin', 'healthcareadmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy', 'patient'],
    'viewAll': ['hospitaladmin', 'healthcareadmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy'],
    'viewSelf': ['patient', 'doctor', 'remotedoctor', 'healthcareassistant', 'lab', 'labtechnician','nursemidwife','primaryhealthcareprovider', 'pharmacy', 'hospitaladmin', 'healthcareadmin'],
    'update': ['hospitaladmin', 'healthcareadmin'],
    'delete': ['hospitaladmin', 'healthcareadmin'],
  },
  location: {
    add: ['hospitaladmin'],
    update: ['hospitaladmin'],
    viewAll: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    viewSelf: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    delete: ['hospitaladmin'],
    export: ['hospitaladmin']
  },
  practitioner: {
    add: ['hospitaladmin'],
    register: ['hospitaladmin'],
    viewAll: ['hospitaladmin'],
    viewSelf: ['practitioner', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'hospitaladmin'],
    update: ['hospitaladmin'],
    edit: ['hospitaladmin'],
    delete: ['hospitaladmin'],
    approve: ['hospitaladmin'],
    reject: ['hospitaladmin'],
    export: ['hospitaladmin']
  },
  medication: {
    'stock': ['hospitaladmin', 'pharmacist'],
    'add': ['hospitaladmin', 'pharmacist'],
    'edit': ['hospitaladmin', 'pharmacist'],
    'viewAll': ['hospitaladmin', 'pharmacist'],
    'viewSelf': ['hospitaladmin', 'pharmacist'],
    'update': ['hospitaladmin', 'pharmacist'],
  },
  appointment: {
    'book': ['hospitaladmin', 'healthcareadmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy', 'patient'],
    'viewAll': ['hospitaladmin', 'healthcareadmin'],
    'viewSelf': ['patient', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy', 'hospitaladmin', 'healthcareadmin'],
    'updateSelf': ['patient', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy', 'hospitaladmin', 'healthcareadmin'],
    'updateAll': ['hospitaladmin', 'healthcareadmin'],
    'delete': ['hospitaladmin', 'healthcareadmin'],
    'assign': ['hospitaladmin', 'healthcareadmin'],
  },
  medicationRequest: {
    request: ['doctor', 'remotedoctor', 'nursemidwife','primaryhealthcareprovider', 'alliedhealthprofessional'],
    revoke: ['doctor', 'remotedoctor', 'nursemidwife','primaryhealthcareprovider', 'alliedhealthprofessional'],
    viewAll: ['hospitaladmin', 'healthcareadmin', 'doctor', 'remotedoctor', 'healthcareassistant', 'nursemidwife','primaryhealthcareprovider','lab', 'labtechnician', 'pharmacist', 'patient', 'cashier'],
    viewSelf: ['patient', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacist', 'hospitaladmin', 'healthcareadmin'],
    update: ['doctor', 'remotedoctor', 'nursemidwife','primaryhealthcareprovider', 'alliedhealthprofessional'],
    delete: ['doctor', 'remotedoctor', 'nursemidwife','primaryhealthcareprovider', 'alliedhealthprofessional'],
    dispense: ['pharmacist'],
    administer: ['healthcareassistant','nursemidwife','primaryhealthcareprovider', 'doctor', 'remotedoctor'],
    viewDispensed: ['pharmacist', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
  },
  medicationDispense: {
    add: ['pharmacist'],
    viewAll: ['hospitaladmin', 'healthcareadmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacist', 'patient', 'cashier'],
    viewSelf: ['patient', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacist', 'hospitaladmin', 'healthcareadmin'],
    update: ['pharmacist'],
    delete: ['pharmacist'],
    dispense: ['pharmacist'],
    administer: ['healthcareassistant','nursemidwife','primaryhealthcareprovider', 'doctor', 'remotedoctor'],
    viewDispensed: ['pharmacist', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
  },
  specimen: {
    add: ['lab', 'labtechnician'],
    edit: ['lab', 'labtechnician'],
    viewAll: ['lab', 'labtechnician', 'doctor', 'remotedoctor'],
    viewSelf: ['patient', 'lab', 'labtechnician', 'doctor', 'remotedoctor']
  },
  supplyDelivery: {
    // SupplyDelivery is used to track non-medication stock items (materials, reagents, consumables, etc.)
    viewAll: ['hospitaladmin', 'lab', 'labtechnician', 'pharmacy'],
    add: ['hospitaladmin', 'lab', 'labtechnician'],
    editStock: ['hospitaladmin', 'lab', 'labtechnician'],
  },
  labRequest: {
    add: ['doctor', 'remotedoctor', 'nursemidwife','primaryhealthcareprovider', 'alliedhealthprofessional'],
    request: ['doctor', 'remotedoctor', 'nursemidwife','primaryhealthcareprovider', 'alliedhealthprofessional'],
    approve: ['doctor', 'remotedoctor'],
    revoke: ['doctor', 'remotedoctor'],
    cancel: ['doctor', 'remotedoctor', 'lab', 'nursemidwife', 'primaryhealthcareprovider'],
    viewAll: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician'],
    viewSelf: ['patient'],
    update: ['doctor', 'remotedoctor', 'lab', 'labtechnician'],
    enterResult: ['lab', 'labtechnician'],
    reviewResult: ['lab', 'labtechnician', 'doctor', 'remotedoctor'],
    releaseResult: ['lab', 'labtechnician', 'doctor', 'remotedoctor'],
    collectSpecimen: ['healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician'],
    receiveSpecimen: ['lab', 'labtechnician'],
    viewSpecimen: ['lab', 'labtechnician', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    billable: ['cashier', 'hospitaladmin'],
    fulfill: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'alliedhealthprofessional'], // various specialists fulfill services

  },
  serviceRequest: {
    request: ['doctor', 'remotedoctor', 'nursemidwife','primaryhealthcareprovider', 'alliedhealthprofessional'], // order services: imaging, procedures, consults, therapy
    add: ['doctor', 'remotedoctor', 'nursemidwife','primaryhealthcareprovider', 'alliedhealthprofessional'], // create new service requests
    approve: ['doctor', 'remotedoctor'], // senior staff approve high-cost or complex services
    revoke: ['doctor', 'remotedoctor', 'nursemidwife', 'primaryhealthcareprovider'], // ordering provider can cancel
    cancel: ['doctor', 'remotedoctor', 'hospitaladmin', 'nursemidwife', 'primaryhealthcareprovider'], // cancel service orders
    viewAll: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'healthcareadmin', 'alliedhealthprofessional'], // administrative and clinical staff
    viewSelf: ['patient'], // patients see their own service orders
    update: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'alliedhealthprofessional'], // modify service request details
    fulfill: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'alliedhealthprofessional'], // various specialists fulfill services
    schedule: ['healthcareadmin', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'hospitaladmin'], // schedule service appointments
    complete: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'alliedhealthprofessional'], // mark service as completed
    billable: ['cashier', 'hospitaladmin'], // billing and payment processing
    assign: ['hospitaladmin', 'healthcareadmin'], // assign service to specific provider/department
  },
  diagnosticReport: {
    request: ['doctor', 'remotedoctor', 'nursemidwife', 'primaryhealthcareprovider'],
    add: ['lab', 'labtechnician', 'doctor', 'remotedoctor', 'alliedhealthprofessional', 'nursemidwife', 'primaryhealthcareprovider'],
    update: ['lab', 'labtechnician', 'doctor', 'remotedoctor', 'alliedhealthprofessional', 'nursemidwife', 'primaryhealthcareprovider'],
    viewAll: ['doctor', 'remotedoctor', 'lab', 'labtechnician', 'alliedhealthprofessional', 'nursemidwife', 'primaryhealthcareprovider'],
    view: ['doctor', 'remotedoctor', 'lab', 'labtechnician', 'alliedhealthprofessional', 'nursemidwife', 'primaryhealthcareprovider'],
    viewSelf: ['patient']
  },
  observation: {
    add: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'alliedhealthprofessional', 'healthcareadmin'],
    viewAll: ['hospitaladmin', 'doctor', 'healthcareadmin', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy'],
    view: ['hospitaladmin', 'healthcareadmin','doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy', 'alliedhealthprofessional'],
    viewSelf: ['patient', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'hospitaladmin', 'healthcareadmin', 'alliedhealthprofessional'],
    update: ['doctor', 'remotedoctor', 'healthcareassistant', 'nursemidwife','primaryhealthcareprovider','lab', 'labtechnician', 'alliedhealthprofessional'],
    delete: ['hospitaladmin'],
    changeStatus: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'alliedhealthprofessional'],
    enterResult: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'alliedhealthprofessional']
  },
  condition: {
    add: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    viewAll: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy'],
    viewSelf: ['patient', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'hospitaladmin', 'healthcareadmin'],
    update: ['doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    delete: ['hospitaladmin'],
    export: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy']
  },
  codeSystem: {
    add: ['hospitaladmin'],
    viewAll: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy'],
    view: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy'],
    update: ['hospitaladmin'],
    delete: ['hospitaladmin'],
    export: ['hospitaladmin']
  },
  valueSet: {
    add: ['hospitaladmin'],
    viewAll: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy'],
    view: ['hospitaladmin', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider', 'lab', 'labtechnician', 'pharmacy'],
    update: ['hospitaladmin'],
    delete: ['hospitaladmin'],
    export: ['hospitaladmin']
  },
  chargeItemDef: {
    add: ['hospitaladmin', 'cashier'],
    viewAll: ['hospitaladmin', 'cashier', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    view: ['hospitaladmin', 'cashier', 'doctor', 'remotedoctor', 'healthcareassistant','nursemidwife','primaryhealthcareprovider'],
    update: ['hospitaladmin', 'cashier'],
    delete: ['hospitaladmin'],
    export: ['hospitaladmin', 'cashier']
  }
};
// wZy4%fsn4EcQ
